/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import {
  Evaluator,
  EvaluatorForStudent,
  JobDetails,
  Mentee,
  MenteeAssignment,
  Mentor,
  MentorshipImportReport,
  MentorshipStatus,
  Student,
  StudentAssignment,
  StudentForEvaluator,
} from '@app/app/page-modules/admin/components/mentors/admin-mentors.model';
import { CommentRequest } from '@app/app/page-modules/reviews/models';
import { MembersBooleanSearchRequest } from '@app/app/shared/models/admin/boolean-filters.model';
import { UserSearch } from '@app/app/shared/models/admin/members.model';
import { Page } from '@app/app/shared/models/page';
import { ObservableResult } from '@app/app/shared/store';
import {
  CreateJobRequest,
  MenteePlaylistSummary,
  MentorFeedback,
  MentorFeedbackEventNote,
  MentorMenteeDataService,
  PendingMenteesDecisionRequest,
  RelatedUserSearchItem,
} from './mentor-mentee-data.service';

@Injectable()
export class MockMentorMenteeDataService implements MentorMenteeDataService {
  getAllJobs(): ObservableResult<JobDetails[]> {
    return ObservableResult.ofSuccess();
  }
  createJob(request: CreateJobRequest): ObservableResult<JobDetails> {
    return ObservableResult.ofSuccess();
  }
  assignJobToStudentEvaluator(evaluatorUserUid: string, studentUserUid: string, jobId: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  removeJobFromStudentEvaluator(evaluatorUserUid: string, studentUserUid: string, jobId: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  searchByMentor(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Mentor>> {
    return ObservableResult.ofSuccess();
  }

  searchByMentee(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Mentee>> {
    return ObservableResult.ofSuccess();
  }

  searchByEvaluator(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Evaluator>> {
    return ObservableResult.ofSuccess();
  }

  searchByStudent(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Student>> {
    return ObservableResult.ofSuccess();
  }

  findStudentsForEvaluator(
    page: number,
    size: number,
    evaluatorUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<StudentForEvaluator>> {
    return ObservableResult.ofSuccess();
  }

  findEvaluatorsForStudent(
    page: number,
    size: number,
    studentUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<EvaluatorForStudent>> {
    return ObservableResult.ofSuccess();
  }

  findMenteesForMentor(
    page: number,
    size: number,
    mentorUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<Mentee>> {
    return ObservableResult.ofSuccess();
  }

  findMentorsForMentee(
    page: number,
    size: number,
    menteeUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<Mentor>> {
    return ObservableResult.ofSuccess();
  }

  findMentors(page: number, size: number, term?: string, statuses?: MentorshipStatus[]): ObservableResult<Page<Mentor>> {
    return ObservableResult.ofSuccess();
  }

  assignMenteeToMentor(mentorUserUid: string, menteeUserUid: string, status: MentorshipStatus): ObservableResult<MenteeAssignment> {
    return ObservableResult.ofSuccess();
  }

  deleteMenteeFromMentor(mentorUserUid: string, menteeUserUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  assignStudentToEvaluator(
    evaluatorUserUid: string,
    studentUserUid: string,
    status: MentorshipStatus,
  ): ObservableResult<StudentAssignment> {
    return ObservableResult.ofSuccess();
  }

  deleteStudentFromEvaluator(evaluatorUserUid: string, studentUserUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  importMentees(menteesFile: File, sendNotifications: boolean): ObservableResult<MentorshipImportReport> {
    return ObservableResult.ofSuccess();
  }

  postPendingMenteesDecision(decision: PendingMenteesDecisionRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  findPendingMentees(): ObservableResult<UserSearch[]> {
    return ObservableResult.ofSuccess();
  }

  getAllFeedbackForLearnerPlaylist(playlistUid: string, learnerUserUid: string): ObservableResult<MentorFeedback[]> {
    return ObservableResult.ofSuccess();
  }

  updateNoteOnForm(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    learnerUserUid: string,
    noteUid: string,
    request: CommentRequest,
  ): ObservableResult<MentorFeedbackEventNote> {
    return ObservableResult.ofSuccess();
  }
  createNoteOnForm(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    learnerUserUid: string,
    request: CommentRequest,
  ): ObservableResult<MentorFeedbackEventNote> {
    return ObservableResult.ofSuccess();
  }
  getSingleMentorFeedbackForLearnerPlaylist(
    playlistUid: string,
    learnerUserUid: string,
    mentorUserUid: string,
  ): ObservableResult<MentorFeedback> {
    return ObservableResult.ofSuccess();
  }
  deleteNoteOnForm(playlistUid: string, cardUid: string, formUid: string, learnerUserUid: string, noteUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  getSubmissionsFromMentee(
    mentorUserUid: string,
    menteeUserUid: string,
    page: number,
    size: number,
  ): ObservableResult<Page<MenteePlaylistSummary>> {
    return ObservableResult.ofSuccess();
  }
  getMentee(menteeUserUid: string): ObservableResult<Mentee> {
    return ObservableResult.ofSuccess();
  }
  patchFormAcknowledgement(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    menteeUserUid: string,
    isAcknowledged: boolean,
  ): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }
  getRelatedUser(relatedUserUid: string): ObservableResult<RelatedUserSearchItem> {
    return ObservableResult.ofSuccess();
  }
}
